import React from 'react';
import * as Store from '@apply/store';
import {
    CertificateOfOccupancyType,
    CertificateOfOccupancyTypeOptions,
    ExitType,
    ExitTypeOptions,
    LoanType,
    PreferredTermTypeOptions,
    PropertyDevelopmentType,
    PropertyDevelopmentTypeOptions,
} from '@apply/constants';
import { Accordion, ShowIf, buildTypeString, addDays, formatCurrency, DataRow, PropertySubheader, formatDate } from '@apply/ui';

export interface BridgePropertyReviewProps {
    property: Store.PropertiesStore.Types.Property;
    index: number;
    apiId?: string;
    printMode: boolean;
    formCompleted: boolean;
    loanType?: LoanType;
    token: string;
    collapsable: boolean;
}

export const BridgePropertyReview: React.FC<BridgePropertyReviewProps> = ({
    property,
    index,
    apiId,
    printMode,
    formCompleted,
    loanType,
    token,
    collapsable,
}) => {
    const bridge = property?.nonRental;

    const provideLaterAdvisory = Store.PropertiesStore.Types.constructReviewFormsAdvisoryMessagePrefix(loanType, property);

    const isDateOfAcquisitionInFuture = (): boolean => {
        const dateOfAcquisition = new Date(bridge?.acquisitionDate);

        const today = new Date();

        return dateOfAcquisition.getTime() > today.getTime();
    };

    const isDateOfAcquisitionOverNinetyDays = (): boolean => {
        const dateOfAcquisition = new Date(bridge?.acquisitionDate);
        const ninetyDaysFromAcquisition = addDays(dateOfAcquisition, 90);

        const today = new Date();

        return today.getTime() > ninetyDaysFromAcquisition.getTime();
    };

    const isChallengingTimeTable = (): boolean => {
        if (bridge?.isAquisitionFinancing !== true) return false;

        const dateOfAcquisition = new Date(bridge?.acquisitionDate);
        const twentyOneDaysBeforeAcquisition = addDays(dateOfAcquisition, -21);

        const today = new Date();

        return today.getTime() > twentyOneDaysBeforeAcquisition.getTime();
    };

    const wasBuiltBefore1981 = (): boolean => {
        return bridge?.yearBuilt < 1981;
    };

    const calloutRedNote = (showIf: boolean, text: string) => {
        return (
            <ShowIf showIf={showIf && printMode}>
                <div className='row'>
                    <div style={{ width: '50%' }}>&nbsp;</div>
                    <div style={{ width: '50%', color: 'red' }}>
                        <b>{text}</b>
                    </div>
                </div>
            </ShowIf>
        );
    };

    return (
        <>
            <div className='scrollable-content'>
                <div
                    className='accordion gray-adjust'
                    data-accordion=''
                    data-allow-all-closed='true'
                    data-multi-expand='true'
                >
                    <Accordion
                        title={
                            <PropertySubheader
                                index={index}
                                property={property}
                                apiId={apiId}
                                formCompleted={formCompleted}
                                token={token}
                                printMode={printMode}
                            />
                        }
                        isCollapsable={collapsable}
                    >
                        <DataRow value={property?.structureType}>Property Structure</DataRow>

                        <ShowIf showIf={(bridge?.numberUnits ?? 0) > 0}>
                            <DataRow value={bridge?.numberUnits}>Units</DataRow>
                            <DataRow value={bridge?.unitsIncomeRestricted}>
                                Units subject to income restricted federal, state, or local housing program
                            </DataRow>
                        </ShowIf>

                        <DataRow value={property?.address}>Property Address</DataRow>

                        <DataRow value={PropertyDevelopmentTypeOptions.find((o) => o.id == bridge?.propertyDevelopmentType)}>
                            Project Type
                        </DataRow>

                        <ShowIf
                            showIf={
                                bridge?.propertyDevelopmentType !== undefined &&
                                [
                                    PropertyDevelopmentType.NewConstruction,
                                    PropertyDevelopmentType.Renovation,
                                    PropertyDevelopmentType.RenovationAndExpansion,
                                ].includes(bridge.propertyDevelopmentType)
                            }
                        >
                            <DataRow value={bridge?.yearBuilt}>Year Built</DataRow>
                            {calloutRedNote(wasBuiltBefore1981(), 'ASBESTOS RISK')}
                            <DataRow value={bridge?.ownedThroughoutDevelopment}>
                                Did you own the Property through the construction or renovation
                            </DataRow>
                        </ShowIf>

                        <ShowIf showIf={bridge?.propertyDevelopmentType == PropertyDevelopmentType.NewAcquisition}>
                            <DataRow value={formatDate(new Date(bridge?.acquisitionDate))}>
                                Date of acquisition (actual or expected)
                            </DataRow>
                            {calloutRedNote(isDateOfAcquisitionOverNinetyDays(), 'OWNED A WHILE')}
                            {calloutRedNote(isChallengingTimeTable(), 'CHALLENGING TIMETABLE')}
                            <ShowIf showIf={isDateOfAcquisitionInFuture()}>
                                <DataRow value={bridge?.isAquisitionFinancing}>
                                    Is acquisition funding required at closing?
                                </DataRow>
                            </ShowIf>
                        </ShowIf>

                        <ShowIf showIf={bridge?.propertyDevelopmentType == PropertyDevelopmentType.Other}>
                            <DataRow value={bridge?.propertyDevelopmentOther}>Other Description</DataRow>
                        </ShowIf>

                        <DataRow value={formatCurrency(bridge?.currentDebt, 0)}>
                            Current debt on property (enter $0 if no debt you owe)
                        </DataRow>

                        <DataRow value={bridge?.landSize}>Land Size</DataRow>

                        <DataRow value={buildTypeString(bridge?.buildType)}>Is it a Site Built or Manufactured home?</DataRow>

                        <DataRow value={CertificateOfOccupancyTypeOptions.find((t) => t.id == bridge?.certificateOfOccupancy)}>
                            Does the property have a certificate of occupancy or local equivalent meaning that it can be legally
                            inhabited?*
                        </DataRow>
                        <ShowIf showIf={bridge?.certificateOfOccupancy !== CertificateOfOccupancyType.No}>
                            <DataRow value={bridge?.proofOfOccupancy}>Proof of Occupancy</DataRow>
                        </ShowIf>

                        <DataRow value={bridge?.workNeeded}>Does any work need to be completed on the property?</DataRow>
                        <ShowIf showIf={bridge?.workNeeded}>
                            <DataRow value={bridge?.workNeededDescription}>Describe remaining work</DataRow>
                        </ShowIf>

                        <DataRow value={bridge?.hoaDues}>HOA Dues</DataRow>

                        <DataRow value={bridge?.survey}>Survey</DataRow>

                        <DataRow value={ExitTypeOptions.find((t) => t.id == bridge?.exitType)}>Exit Type</DataRow>
                        <ShowIf showIf={bridge?.exitType == ExitType.Sale}>
                            <DataRow value={formatCurrency(bridge?.expectedListingPrice, 0)}>
                                {property.structureType === 'Duplex' ||
                                property.structureType === 'Triplex' ||
                                property.structureType === 'Quadplex' ||
                                property.structureType === '5+ Unit'
                                    ? 'Total Expected Sale Price'
                                    : 'Expected Sale Price'}
                            </DataRow>
                        </ShowIf>
                        <ShowIf showIf={bridge?.exitType == ExitType.Refinance}>
                            <DataRow value={formatCurrency(bridge?.expectedTakeOutLoanAmount, 0)}>
                                {property.structureType === 'Duplex' ||
                                property.structureType === 'Triplex' ||
                                property.structureType === 'Quadplex' ||
                                property.structureType === '5+ Unit'
                                    ? 'Total Expected Take-Out Loan Amount'
                                    : 'Expected Take-Out Loan Amount'}
                            </DataRow>
                        </ShowIf>

                        <DataRow value={PreferredTermTypeOptions.find((t) => t.id == bridge?.preferredTermType)}>
                            Preferred Term
                        </DataRow>

                        <DataRow value={formatCurrency(bridge?.targetLoanRequest, 0)}>Target Loan Request</DataRow>

                        <ShowIf showIf={!!provideLaterAdvisory}>
                            <div className='row'>
                                <div
                                    style={{
                                        paddingRight: '50px',
                                        paddingLeft: '50px',
                                    }}
                                >
                                    <strong>
                                        <em>NOTE:</em>
                                    </strong>
                                    &nbsp; {provideLaterAdvisory} are/is needed to process your application and order a valuation.
                                    Please submit ASAP via&nbsp;
                                    <a href='mailto:team@builderfinance.com'>team@builderfinance.com</a>
                                    &nbsp; or&nbsp;
                                    <a
                                        href='https://fileshare.bfiweb.com'
                                        target='_blank'
                                    >
                                        https://fileshare.bfiweb.com
                                    </a>
                                </div>
                            </div>
                        </ShowIf>
                    </Accordion>
                </div>
            </div>
        </>
    );
};
